import { addMinutes, isAfter, isBefore, isPast, isValid, subMinutes } from 'date-fns';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { LuX } from 'react-icons/lu';
import { toast } from 'sonner';

import { GUID } from '@ndvr/auth-api';
import { MeetingProvider, PersonOrg, PersonOrgProfile, SuggestedMeetingView } from '@ndvr/customer-api';

import { DateTimePicker } from '@/components/DateTimePicker';
import { Button } from '@/components/ui/button';
import { ComboboxOption, FormCombobox } from '@/components/ui/combobox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { FormTextarea } from '@/components/ui/textarea';
import { useIsZoomConnected } from '@/views/calendar/calendar-queries';
import { useAutoScheduleMeetingBotPreference } from '@/views/preferences/user-preference-queries';
import { getHouseholdName, parseLocalDate } from 'utils';
import { MeetingLink, useCreateEvent } from 'views/calendar/event-queries';
import { useCommandCenterHouseholds } from 'views/household/household-queries';
import { usePersonOrgProfilesForHousehold } from 'views/person/person-queries';

const MINIMUM_EVENT_DURATION_MINUTES = 30;

interface NewEventFormData {
    title: string;
    description: string;
    startTime: string;
    endTime: string;
    meetingBotEnabled: boolean;
    meetingProvider: MeetingProvider;
    household: { value: string; label: string };
    participants: ParticipantInfo[];
}

interface ParticipantInfo {
    email: string;
    personOrgGUID?: GUID<PersonOrg>;
    isKnown: boolean;
}

export type NewEventPrefill = Partial<Pick<NewEventFormData, 'title' | 'description' | 'startTime' | 'endTime'>>;

// Prefill for scheduling one of Claire's suggested meetings. The picker holds ISO strings, and the
// server's DateTimeStr goes through the repo's parser rather than a bare new Date(). Either time is
// absent when the transcript named no concrete slot; the sheet's own defaults then fill the gap.
export const toSuggestedMeetingFormValues = (suggestion: SuggestedMeetingView): NewEventPrefill => ({
    title: suggestion.title || '',
    description: suggestion.description || '',
    ...(suggestion.suggestedStartTime && { startTime: parseLocalDate(suggestion.suggestedStartTime).toISOString() }),
    ...(suggestion.suggestedEndTime && { endTime: parseLocalDate(suggestion.suggestedEndTime).toISOString() }),
});

export interface NewEventSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    trigger?: React.ReactNode;
    defaultHousehold?: { value: string; label: string };
    disableHouseholdSelection?: boolean;
    defaultValues?: NewEventPrefill;
    link?: MeetingLink;
}

export const NewEventSheet = ({
    open,
    onOpenChange,
    trigger,
    defaultHousehold,
    disableHouseholdSelection,
    defaultValues,
    link,
}: NewEventSheetProps) => {
    const [participantEmail, setParticipantEmail] = useState('');
    const [showParticipantDropdown, setShowParticipantDropdown] = useState(false);
    const dropdownRef = React.useRef<HTMLDivElement>(null);

    const { data: households } = useCommandCenterHouseholds();
    const { data: autoScheduleMeetingBot = true } = useAutoScheduleMeetingBotPreference();

    // Set default start and end times
    const now = new Date();
    const oneHourLater = new Date(now);
    oneHourLater.setHours(now.getHours() + 1);

    const form = useForm<NewEventFormData>({
        defaultValues: {
            title: '',
            description: '',
            startTime: now.toISOString(),
            endTime: oneHourLater.toISOString(),
            meetingBotEnabled: true,
            meetingProvider: MeetingProvider.ZOOM,
            household: defaultHousehold || { value: '', label: '' },
            participants: [] as ParticipantInfo[],
            ...defaultValues,
        },
        mode: 'onChange',
    });

    const { mutateAsync: createEvent, isPending } = useCreateEvent();
    const { data: isZoomConnected = false } = useIsZoomConnected();

    useEffect(() => {
        if (open && !form.formState.isDirty) {
            form.setValue('meetingBotEnabled', autoScheduleMeetingBot);
        }
    }, [autoScheduleMeetingBot, form, open]);

    const handleStartTimeChange = (newStartTime: string) => {
        form.setValue('startTime', newStartTime);
        const startDate = new Date(newStartTime);
        const endDate = new Date(form.getValues('endTime'));

        if (isValid(startDate) && isValid(endDate) && isAfter(startDate, endDate)) {
            form.setValue('endTime', addMinutes(startDate, MINIMUM_EVENT_DURATION_MINUTES).toISOString());
        }

        form.trigger('endTime');
    };

    const handleEndTimeChange = (newEndTime: string) => {
        form.setValue('endTime', newEndTime);
        const startDate = new Date(form.getValues('startTime'));
        const endDate = new Date(newEndTime);

        if (isValid(endDate) && isValid(startDate) && isBefore(endDate, startDate)) {
            form.setValue('startTime', subMinutes(endDate, MINIMUM_EVENT_DURATION_MINUTES).toISOString());
        }

        form.trigger('startTime');
    };

    const handleSubmit = async (data: NewEventFormData) => {
        try {
            const startDate = new Date(data.startTime);
            const endDate = new Date(data.endTime);

            if (isPast(startDate)) {
                toast.error('Start time cannot be in the past');
                return;
            }

            if (isPast(endDate)) {
                toast.error('End time cannot be in the past');
                return;
            }

            if (!isZoomConnected) {
                toast.error('Zoom is not connected. Please connect Zoom before creating an event.');
                return;
            }

            // Extract household GUID from FormCombobox format
            const householdGUID = data.household?.value || '';

            if (!householdGUID) {
                toast.error('Please select a household');
                return;
            }

            // Separate known and unknown participants
            const knownParticipants = data.participants
                .filter((p) => p.isKnown && p.personOrgGUID)
                .map((p) => p.personOrgGUID!);

            const unknownParticipants = data.participants
                .filter((p) => !p.isKnown || !p.personOrgGUID)
                .map((p) => p.email);

            await createEvent({
                householdGUID,
                request: {
                    title: data.title,
                    description: data.description,
                    scheduleMeetingBot: data.meetingBotEnabled,
                    startTime: data.startTime,
                    endTime: data.endTime,
                    knownParticipants,
                    unknownParticipants,
                    online: true, // Default to online
                    meetingProvider: data.meetingProvider,
                    ...(link?.suggestedMeetingGUID && { suggestedMeetingGUID: link.suggestedMeetingGUID }),
                },
                noteGUID: link?.noteGUID,
            });
            onOpenChange(false);
            const resetStartTime = new Date();
            const resetEndTime = new Date(resetStartTime);
            resetEndTime.setHours(resetStartTime.getHours() + 1);
            form.reset({
                title: '',
                description: '',
                startTime: resetStartTime.toISOString(),
                endTime: resetEndTime.toISOString(),
                meetingBotEnabled: autoScheduleMeetingBot,
                meetingProvider: MeetingProvider.ZOOM,
                household: defaultHousehold || { value: '', label: '' },
                participants: [],
                ...defaultValues,
            });
        } catch (error) {
            // TODO: Add proper error handling/toast notification
        }
    };

    const selectedHousehold = form.watch('household');
    const householdGUID = selectedHousehold?.value || '';

    const { data: linkedPersonOrgProfiles } = usePersonOrgProfilesForHousehold(householdGUID, {
        enabled: !!householdGUID,
    });

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowParticipantDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const addParticipant = () => {
        if (participantEmail && !form.getValues('participants').some((p) => p.email === participantEmail)) {
            const currentParticipants = form.getValues('participants');
            form.setValue('participants', [...currentParticipants, { email: participantEmail, isKnown: false }]);
            setParticipantEmail('');
            setShowParticipantDropdown(false);
        }
    };

    const addParticipantFromProfile = (email: string, personOrgGUID?: GUID<PersonOrg>) => {
        if (email && !form.getValues('participants').some((p) => p.email === email)) {
            const currentParticipants = form.getValues('participants');
            form.setValue('participants', [...currentParticipants, { email, personOrgGUID, isKnown: true }]);
            setParticipantEmail('');
            setShowParticipantDropdown(false);
        }
    };

    const removeParticipant = (email: string) => {
        const currentParticipants = form.getValues('participants');
        form.setValue(
            'participants',
            currentParticipants.filter((p) => p.email !== email),
        );
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addParticipant();
        }
        if (e.key === 'Escape') {
            setShowParticipantDropdown(false);
        }
    };

    // Filter available profiles based on current input and exclude already added participants
    const availableProfiles =
        linkedPersonOrgProfiles?.filter((profile: PersonOrgProfile) => {
            const email = profile.personOrg.primaryEmail?.plainText;
            const firstName = profile.personOrg.firstName?.plainText || '';
            const lastName = profile.personOrg.lastName?.plainText || '';
            const name = `${firstName} ${lastName}`.trim();
            const currentParticipants = form.getValues('participants');

            // If no search input, show all profiles with emails (except already added participants)
            if (!participantEmail.trim()) {
                return email && !currentParticipants.some((p) => p.email === email);
            }

            // If there's search input, filter by name or email
            return (
                email &&
                !currentParticipants.some((p) => p.email === email) &&
                (email.toLowerCase().includes(participantEmail.toLowerCase()) ||
                    name.toLowerCase().includes(participantEmail.toLowerCase()))
            );
        }) || [];

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            {trigger && <SheetTrigger asChild>{trigger}</SheetTrigger>}
            <SheetContent side="right" className="w-3/4 overflow-y-auto sm:w-96" aria-describedby={undefined}>
                <SheetHeader className="mb-4">
                    <SheetTitle>
                        <span className="text-accent text-lg font-semibold">Create new event</span>
                    </SheetTitle>
                </SheetHeader>

                <div>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                            <FormField
                                control={form.control}
                                name="title"
                                rules={{ required: 'Event title is required' }}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Title *</FormLabel>
                                        <FormControl>
                                            <Input {...field} placeholder="Event title" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormTextarea
                                formMethods={form}
                                name="description"
                                label="Description"
                                placeholder="Event description"
                                rows={4}
                            />

                            <div className="grid grid-cols-1 gap-4">
                                <FormField
                                    control={form.control}
                                    name="startTime"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Start Time</FormLabel>
                                            <FormControl>
                                                <DateTimePicker
                                                    value={field.value}
                                                    onChange={handleStartTimeChange}
                                                    placeholder="Select start date"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="endTime"
                                    rules={{
                                        required: 'End time is required',
                                        validate: (end) => {
                                            const start = form.getValues('startTime');
                                            if (!start || !end) return true; // let "required" handle empties
                                            return (
                                                new Date(end) > new Date(start) || 'End time must be after start time'
                                            );
                                        },
                                    }}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>End Time</FormLabel>
                                            <FormControl>
                                                <DateTimePicker
                                                    value={field.value}
                                                    onChange={handleEndTimeChange}
                                                    placeholder="Select end date"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <div className="flex items-center justify-between rounded-md border p-3">
                                <div>
                                    <div className="font-medium">Invite Claire&#8482;</div>
                                    <div className="text-muted-foreground text-sm">
                                        Claire &#8482; automatically connects to take meeting notes
                                    </div>
                                </div>
                                <FormField
                                    control={form.control}
                                    name="meetingBotEnabled"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl>
                                                <div
                                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                                        field.value ? 'bg-primary' : 'bg-gray-300'
                                                    }`}
                                                    onClick={() => field.onChange(!field.value)}
                                                >
                                                    <span
                                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                                            field.value ? 'translate-x-6' : 'translate-x-1'
                                                        }`}
                                                    />
                                                </div>
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <FormField
                                control={form.control}
                                name="meetingProvider"
                                render={({ field }) => (
                                    <FormItem>
                                        <div className="flex items-center gap-1">
                                            <FormLabel>Meeting Provider</FormLabel>
                                        </div>
                                        <FormControl>
                                            <select
                                                {...field}
                                                disabled={true}
                                                className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                <option value={MeetingProvider.ZOOM}>Zoom</option>
                                            </select>
                                        </FormControl>
                                        {!isZoomConnected && (
                                            <div className="text-destructive mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm">
                                                Zoom is not connected. Please connect Zoom before creating an event.
                                            </div>
                                        )}
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {disableHouseholdSelection ? (
                                <FormItem>
                                    <FormLabel>Household</FormLabel>
                                    <FormControl>
                                        <div className="text-muted-foreground">
                                            {selectedHousehold.label ||
                                                getHouseholdName(
                                                    households?.find((h) => h.globalAccountGUID === householdGUID),
                                                )}
                                        </div>
                                    </FormControl>
                                </FormItem>
                            ) : (
                                <FormCombobox
                                    formMethods={form}
                                    name="household"
                                    options={
                                        (households?.map((household) => ({
                                            value: household.globalAccountGUID,
                                            label: getHouseholdName(household) || 'Unnamed Household',
                                        })) || []) satisfies ComboboxOption[]
                                    }
                                    label="Household *"
                                    selectOptionLabel="Select a household"
                                    noOptionsLabel="No households found"
                                    placeholder="Select a household"
                                    required
                                />
                            )}

                            <FormItem>
                                <FormLabel>Participants</FormLabel>
                                <div className="relative" ref={dropdownRef}>
                                    <div className="flex gap-2">
                                        <div className="relative flex-1">
                                            <input
                                                type="email"
                                                placeholder="Enter email address or search contacts"
                                                value={participantEmail}
                                                onChange={(e) => {
                                                    setParticipantEmail(e.target.value);
                                                    setShowParticipantDropdown(availableProfiles.length > 0);
                                                }}
                                                onKeyDown={handleKeyDown}
                                                onFocus={() => setShowParticipantDropdown(availableProfiles.length > 0)}
                                                className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                                            />
                                            {showParticipantDropdown && availableProfiles.length > 0 && (
                                                <div className="bg-popover text-popover-foreground absolute top-full right-0 left-0 z-50 mt-1 max-h-60 overflow-auto rounded-md border shadow-md">
                                                    {availableProfiles.map((profile: PersonOrgProfile) => (
                                                        <div
                                                            key={profile.personOrgGUID}
                                                            className="hover:bg-accent hover:text-accent-foreground cursor-pointer px-3 py-2 text-sm"
                                                            onClick={() => {
                                                                const email = profile.personOrg.primaryEmail?.plainText;
                                                                if (email) {
                                                                    addParticipantFromProfile(
                                                                        email,
                                                                        profile.personOrgGUID,
                                                                    );
                                                                }
                                                            }}
                                                        >
                                                            <div className="font-medium">
                                                                {profile.personOrg.firstName?.plainText}{' '}
                                                                {profile.personOrg.lastName?.plainText}
                                                            </div>
                                                            <div className="text-muted-foreground text-xs">
                                                                {profile.personOrg.primaryEmail?.plainText}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <Button type="button" onClick={addParticipant} variant="outline" size="icon">
                                            +
                                        </Button>
                                    </div>
                                </div>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {form.watch('participants').map((participant, index) => (
                                        <div
                                            key={`${participant.email}-${index}`}
                                            className="bg-ndvr-seafoam flex items-center gap-1 rounded-md px-2 py-1 text-sm"
                                        >
                                            <span>{participant.email}</span>
                                            <button
                                                type="button"
                                                onClick={() => removeParticipant(participant.email)}
                                                className="ml-1 text-gray-500 hover:text-gray-700"
                                            >
                                                <LuX size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </FormItem>

                            <SheetFooter className="flex gap-2 pt-6">
                                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={isPending || !form.formState.isValid || !isZoomConnected}
                                >
                                    {isPending ? (
                                        <>
                                            <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                                            Creating...
                                        </>
                                    ) : (
                                        'Create Event'
                                    )}
                                </Button>
                            </SheetFooter>
                        </form>
                    </Form>
                </div>
            </SheetContent>
        </Sheet>
    );
};
